import type { NodeColumnKey } from '../table-clipboard';

/**
 * The node columns the backend types as a LIST — `nextNodes` as `[numeric!]`,
 * the rest as `[String!]` (`Tenancy_Solution_Version_Nodes_Ps_Insert_Input`).
 * A non-list value in any of them is rejected by the node upsert.
 *
 * `satisfies` keeps the NAMES honest — a renamed column stops compiling here.
 * It does NOT notice a NEW array column added to the schema; that one would
 * silently drop out of the coercion, and only the PIF side has a guard for it
 * (`assertNoUntransformedArrays`). Re-check this list when the node schema
 * gains a field.
 */
export const ARRAY_VALUED_NODE_COLUMNS = [
  'behaviors',
  'nextNodes',
  'output',
  'variables',
  'whatsNext',
] as const satisfies readonly NodeColumnKey[];

/** How a raw cell text becomes the column's stored value — the store injects
 *  its own `convertTextToColumnValue`, so there is ONE conversion contract. */
export type ConvertCellText = (text: string, column: string) => unknown;

/**
 * PSTUDIO-4081 — make a row's array-valued columns match their column contract.
 *
 * ## Why a row can be holding a string
 *
 * The Expanded Cell View writes the RAW typed text into the row on every
 * keystroke and canonicalizes it only on commit (PSTUDIO-3882 —
 * `buildPanelCellDraftUpdate` / `buildPanelCellCommitUpdate`); that is
 * deliberate, because splitting on the separator per keystroke made a trailing
 * `|` untypeable. While the draft sits there the row's `whatsNext` is a string,
 * not an array — and the grid used to render such a cell BLANK, because
 * `cell.tsx` joins only `Array.isArray(value)`. So the value was real in the
 * model, visible in the panel, and invisible in the grid.
 *
 * A row copy takes the row as it stands into the full-fidelity clipboard
 * envelope, and `pasteRow` carries it into the target solution. The upsert then
 * sends the string to a `[numeric!]` variable: GraphQL wraps a non-list into a
 * one-item list and fails to coerce `"12,13"` to `numeric`, so the WHOLE chunk
 * is rejected — the "Autosave Error" toast, with the pasted rows sitting in the
 * grid and never reaching the database.
 *
 * ## The conversion this delegates to
 *
 * `convertTextToColumnValue` — the converter the PANEL's commit path uses
 * (`panel-cell-updates.ts`), which is the path that produced the raw draft in
 * the first place. It is NOT the grid's own commit converter: `parseNativeCellRaw`
 * splits on the column's single separator and yields strings, while this one
 * splits on `/[,;|]/`, trims, and yields numbers for `nextNodes`. The two have
 * disagreed since PSTUDIO-2821 and this change does not reconcile them; it only
 * makes sure the shape that reaches the database is a list.
 *
 * ## What it deliberately does NOT do
 *
 * - It does not invent a column the row never carried: the mutation's
 *   `update_columns` would then overwrite a stored value with a default. The
 *   check is `hasOwnProperty`, not `in`, so an inherited property can never be
 *   promoted to an own one.
 * - It leaves `null`/`undefined` alone — both are legal "no value".
 * - It leaves a shape it cannot honestly convert (an object, a boolean) alone
 *   rather than persisting `String({})` as the literal `"[object Object]"`.
 * - **It never replaces a non-empty value with an empty array.** The converters
 *   are all-or-nothing about validity — `convertToVariablesArray` returns `[]`
 *   when ANY name fails `isValidGlobalVariableName`, `convertToBehaviorsArray`
 *   drops unknown behaviors, `convertToNextNodesArray` discards anything that
 *   is not an integer — so a half-typed draft (`"FOO,"`, `"A"`) converts to
 *   nothing at all. Writing that would turn the loud failure this fix removes
 *   into a SILENT one: the upsert would succeed and `update_columns` would
 *   overwrite the stored value with `[]`. Refusing the conversion keeps the
 *   old, visible behaviour for that value — the row still fails the upsert, as
 *   it did before this change.
 *
 *   **The guard is total only for a WHOLLY unconvertible value.** A partially
 *   convertible one still loses its bad tokens quietly: `"12,abc"` becomes
 *   `[12]` and `"inline,bogus"` becomes `["inline"]`. Tightening it to "no
 *   token may be dropped" was considered and rejected: a rejected row fails the
 *   whole upsert CHUNK (up to 100 rows, `saveBulkRowsToDatabase` throws on the
 *   first error), so the cure would cost other rows their save. The narrow
 *   guard is the compromise, and this paragraph is the honest statement of what
 *   it does not cover.
 */
export function coerceRowArrayColumns<T extends object>(
  row: T,
  convert: ConvertCellText
): T {
  const fixes = collectRowArrayColumnFixes(row, convert);
  return fixes ? ({ ...row, ...fixes } as T) : row;
}

/**
 * The patch form of `coerceRowArrayColumns`: the columns that need fixing, or
 * `null` when the row already matches its contract.
 *
 * A caller holding a row it must not rebuild — an immer draft in `pasteRow`,
 * say — assigns only these keys. Writing a whole `{ ...draft }` back into a
 * draft is not merely wasteful: a row that came off the system clipboard can
 * carry an own `__proto__` key (JSON.parse preserves it), and assigning that
 * key onto an immer draft throws `Object.setPrototypeOf() cannot be used on an
 * Immer draft` — a prepared clipboard payload would break paste outright.
 */
export function collectRowArrayColumnFixes<T extends object>(
  row: T,
  convert: ConvertCellText
): Partial<Record<(typeof ARRAY_VALUED_NODE_COLUMNS)[number], unknown>> | null {
  const source = row as Record<string, unknown>;
  let fixes: Record<string, unknown> | null = null;

  for (const column of ARRAY_VALUED_NODE_COLUMNS) {
    if (!Object.prototype.hasOwnProperty.call(source, column)) continue;

    const value = source[column];
    if (Array.isArray(value)) continue;
    // Only the shapes an editor can actually leave behind: the panel's raw
    // draft string, or a number typed as a single node reference.
    if (typeof value !== 'string' && typeof value !== 'number') continue;

    const text = String(value);
    const converted = convert(text, column);
    // See "It never replaces a non-empty value with an empty array" above.
    if (
      Array.isArray(converted) &&
      converted.length === 0 &&
      text.trim() !== ''
    ) {
      continue;
    }

    fixes ??= {};
    fixes[column] = converted;
  }

  return fixes;
}
